import { CSSProperties, useCallback, useEffect, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { DEFAULT_LAYOUT_STYLES } from 'apps/web/constants/pptThemeLayouts';
import { useArrowkeys } from 'apps/web/hooks/useArrowkeys';
import { useSwipeNavigation, SWIPE_INDICATORS } from 'apps/web/hooks/useSwipe';
import { BaseLayoutStyle } from 'apps/web/types/ppt-theme-layouts';
import clsx from 'clsx';

import {
  FONTS,
  PPT_MAKER_THEMES,
  TextAlignment,
  THEME_OPTIONS,
} from '@lessonPlanner/constants';

import { Button } from '../Button';

import {
  PptSlide,
  LayoutStyles,
  ContentFormatter,
} from '../../types/ppt-slide';

import { LAYOUT_COMPONENTS } from './PptSlideLayouts';
type PptFont = (typeof FONTS)[number];
type PptTheme = (typeof THEME_OPTIONS)[number];

interface PptSlideContentProps {
  slides: PptSlide[];
  currentSlide: number;
  selectedFont: PptFont;
  selectedTheme: PptTheme;
  titleSlideStyles: CSSProperties;
  contentSlideStyles: CSSProperties;
  updateCurrentSlide: (value: number | ((prev: number) => number)) => void;
  setCurrentSlide: (value: number | ((prev: number) => number)) => void;
}

export function PptSlideContent(props: PptSlideContentProps) {
  const {
    slides,
    currentSlide,
    selectedFont,
    selectedTheme,
    titleSlideStyles,
    contentSlideStyles,
    updateCurrentSlide,
    setCurrentSlide,
  } = props;

  const [inputValue, setInputValue] = useState(currentSlide + 1);

  const handleSlideChange = (event) => {
    const value = event.target.value;
    setInputValue(value);

    if (value === '') return;

    let newSlide = parseInt(value, 10) - 1;
    if (isNaN(newSlide)) return;

    if (newSlide >= slides.length) {
      newSlide = slides.length - 1;
    }

    if (newSlide >= 0) {
      updateCurrentSlide(newSlide);
    }
  };

  useArrowkeys({
    onLeftPress: () => currentSlide > 0 && updateCurrentSlide(currentSlide - 1),
    onRightPress: () =>
      currentSlide < slides.length - 1 && updateCurrentSlide(currentSlide + 1),
  });

  const navigateLeft = useCallback(() => {
    if (currentSlide > 0) {
      updateCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide, updateCurrentSlide]);

  const navigateRight = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      updateCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide, slides.length, updateCurrentSlide]);

  const [dragX, setDragX] = useState(0);
  const { handleTouchStart, handleTouchMove, handleTouchEnd } =
    useSwipeNavigation({
      onSwipeLeft: navigateRight,
      onSwipeRight: navigateLeft,
      threshold: 50,
      onDrag: setDragX,
    });

  const renderSlideCounter = () => (
    <div className="absolute right-4 top-4 z-10 rounded-full bg-gray-800 px-2 py-1 text-xs text-white md:px-3 md:text-sm">
      <input
        className="w-4 bg-transparent text-center"
        max={slides.length}
        min="1"
        value={inputValue}
        onChange={handleSlideChange}
      />{' '}
      / <span className="ml-1">{slides.length}</span>
    </div>
  );
  const renderProgressBar = () => (
    <div
      className="absolute bottom-0 left-0 h-1 bg-blue-500 transition-all duration-300"
      style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
    />
  );

  const renderSwipeIndicators = ({ direction, opacity }) => (
    <div
      style={{ opacity }}
      className={`absolute inset-y-0 ${direction}-0 w-20 
        ${direction === 'left' ? 'bg-gradient-to-r' : 'bg-gradient-to-l'} 
        from-blue-500/20 to-transparent transition-opacity duration-300`}
    />
  );

  useEffect(() => {
    setInputValue(currentSlide + 1);
  }, [currentSlide]);

  const getTextColor = (isTitle: boolean) => {
    const theme = PPT_MAKER_THEMES[selectedTheme.id];
    if (isTitle && theme.titleText) return theme.titleText;
    if (!isTitle && theme.contentText) return theme.contentText;
    return theme.text;
  };

  const renderTitleSlide = () => {
    return (
      <div
        className="relative h-full overflow-hidden"
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
      >
        {SWIPE_INDICATORS.map(({ direction, getOpacity }) =>
          renderSwipeIndicators({
            direction,
            opacity: getOpacity(dragX),
          }),
        )}
        <div
          className="container flex h-full w-full items-center justify-center p-4 transition-transform duration-300 sm:p-8"
          style={{
            transform: `translateX(${Math.min(Math.max(dragX, -100), 100)}px)`,
            containerType: 'size',
          }}
        >
          <h2
            className="-mt-8 max-w-full break-words px-8 text-center text-[min(5cqw,10cqh)] font-bold"
            style={{
              color: getTextColor(true),
              fontFamily: selectedFont.name,
            }}
          >
            {slides[currentSlide]?.title || 'No Title Available'}
          </h2>
        </div>
      </div>
    );
  };

  const formatContentForPptContent: ContentFormatter = (contentValue) => {
    if (!contentValue) return '';
    if (typeof contentValue === 'string') return contentValue;

    // Handle array content for quadrant layout or beforeAfter layout
    if (Array.isArray(contentValue)) {
      if (contentValue.length > 0 && 'title' in contentValue[0]) {
        return contentValue[0].content || '';
      }
      return contentValue[0]?.content || '';
    }

    if ('content' in contentValue) {
      return { content: contentValue.content, image: contentValue.image };
    }

    return '';
  };

  const renderContentWithTitleSlide = () => {
    const currentSlideData = slides[currentSlide];
    const layout = currentSlideData?.layout || LayoutStyles.DEFAULT;
    const content = currentSlideData?.content;

    const layoutConfig = (DEFAULT_LAYOUT_STYLES[layout] ||
      DEFAULT_LAYOUT_STYLES[LayoutStyles.DEFAULT]) as BaseLayoutStyle;

    const LayoutComponent =
      LAYOUT_COMPONENTS[layout] || LAYOUT_COMPONENTS[LayoutStyles.DEFAULT];

    const titleClassName = layoutConfig.typography?.title?.className || '';
    const contentClassName = layoutConfig.typography?.content?.className || '';

    return (
      <div
        className="relative h-full overflow-hidden"
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
      >
        {SWIPE_INDICATORS.map(({ direction, getOpacity }) =>
          renderSwipeIndicators({
            direction,
            opacity: getOpacity(dragX),
          }),
        )}
        <div
          className="flex h-full flex-col p-4 transition-transform duration-300 sm:p-8"
          style={{
            transform: `translateX(${Math.min(Math.max(dragX, -100), 100)}px)`,
            containerType: 'size',
          }}
        >
          <h2
            className={clsx(
              'mb-2 break-words px-2 pb-1 font-bold sm:mb-4 sm:pb-4',
              titleClassName,
            )}
            style={{
              color: getTextColor(false),
              fontFamily: selectedFont.name,
              textAlign: 'left' as TextAlignment,
              fontSize: 'min(4cqw, 8cqh)',
            }}
          >
            {currentSlideData?.title}
          </h2>

          <div
            className={clsx('flex-1 overflow-hidden', contentClassName)}
            style={{
              color: getTextColor(false),
              fontFamily: selectedFont.name,
            }}
          >
            <LayoutComponent
              content={content}
              fontFamily={selectedFont.name}
              formatContentForPptContent={formatContentForPptContent}
              textColor={getTextColor(false)}
              themeType={selectedTheme.id}
            />
          </div>
        </div>
      </div>
    );
  };

  function renderSlideNavigation() {
    return (
      <div className="absolute bottom-4 right-4 z-10 flex justify-end gap-2">
        <Button
          className="inline-flex h-10 w-10 items-center rounded-full border border-gray-300 bg-gray-600 p-2 text-white shadow-md hover:bg-gray-900 disabled:opacity-50 sm:h-12 sm:w-12"
          disabled={currentSlide === 0}
          onClick={() => setCurrentSlide((prev) => Math.max(0, prev - 1))}
        >
          <ChevronLeftIcon className="size-full" />
        </Button>
        <Button
          className="inline-flex h-10 w-10 items-center rounded-full border border-gray-300 bg-gray-600 p-2 text-white shadow-md hover:bg-gray-900 disabled:opacity-50 sm:h-12 sm:w-12"
          disabled={currentSlide === slides.length - 1}
          onClick={() =>
            setCurrentSlide((prev) => Math.min(slides.length - 1, prev + 1))
          }
        >
          <ChevronRightIcon className="size-full" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-[1080px]">
      <div
        className="absolute inset-0 overflow-hidden rounded-lg border border-gray-200 shadow-md"
        style={{
          backgroundColor: PPT_MAKER_THEMES[selectedTheme.id].background,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: PPT_MAKER_THEMES[selectedTheme.id].text,
          ...(slides[currentSlide]?.content
            ? contentSlideStyles
            : titleSlideStyles),
        }}
      >
        {renderSlideNavigation()}
        {renderSlideCounter()}
        {slides[currentSlide]?.content
          ? renderContentWithTitleSlide()
          : renderTitleSlide()}
      </div>
      {renderProgressBar()}
    </div>
  );
}
